'use client'

import * as SwitchPrimitives from '@radix-ui/react-switch'
import { cva, VariantProps } from 'class-variance-authority'

import { ElementRef, forwardRef } from 'react'
import { OverrideElementProps } from '@/src/components/core/lib/types'
import { cn } from '@/src/components/core/lib/utils'

const switchStyle = cva(
  'focus-visible:ring-ring flex items-center focus-visible:ring-offset-background shrink-0 cursor-pointer rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      color: {
        gray: [
          'data-[state=unchecked]:bg-gray-300',
          'focus-visible:ring-gray-500',
          'data-[state=checked]:bg-purple-600',
        ],
      },
      size: {
        24: 'h-6 w-10 pl-1',
        32: 'h-8 w-14 pl-1',
        44: 'h-11 w-20 pl-1',
      },
    },
    defaultVariants: {
      color: 'gray',
      size: 32,
    },
  }
)

const thumbStyle = cva(
  'pointer-events-none shrink-0 block rounded-full ring-0 transition-all',
  {
    variants: {
      color: {
        gray: ['bg-white'],
      },
      size: {
        24: 'size-4 data-[state=checked]:translate-x-3.75 data-[state=unchecked]:translate-x-0',
        32: 'size-6 data-[state=checked]:translate-x-5.5 data-[state=unchecked]:translate-x-0',
        44: 'size-9 data-[state=checked]:translate-x-8.5 data-[state=unchecked]:translate-x-0',
      },
    },
    defaultVariants: {
      color: 'gray',
      size: 32,
    },
  }
)

export type SwitchProps = OverrideElementProps<
  typeof SwitchPrimitives.Root,
  VariantProps<typeof switchStyle>
>

const Switch = forwardRef<
  ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(({ color, size, className, ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cn(switchStyle({ color, size }), className)}
    {...props}
    ref={ref}
  >
    <SwitchPrimitives.Thumb className={thumbStyle({ color, size })} />
  </SwitchPrimitives.Root>
))
// @ts-ignore
Switch.displayName = SwitchPrimitives.Root.displayName

export { Switch }
